import BemClass from '@upsales/components/Utils/bemClass';
import ClientAttributes from 'App/babel/attributes/Client';
import ClientResource from 'App/resources/Client';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import T from 'Components/Helpers/translate';
import logError from 'App/babel/helpers/logError';
import openModal, { shouldOpenModal } from 'App/services/Modal';

import { Block, Flex, Input, ButtonSelect, Title } from '@upsales/components';
import { PrimaryButton } from '@upsales/components/Buttons';
import { CancelablePromise, makeCancelable } from 'App/babel/helpers/promise';
import { useModalClose } from 'App/components/Modals/Modals';
import type Client from 'App/resources/Model/Client';
import type { DrawerClient } from '../../AddSubaccount';
import { useSelector } from 'App/components/hooks';
import ProspectingClient from 'App/resources/Model/ProspectingClient';
import { addManyAccounts } from 'Store/reducers/AccountReducer';
import { connect } from 'react-redux';
import PendingPurchaseBanner, { PendingPurchaseType } from '../PendingPurchaseBanner';
import SoliditetClient from 'App/resources/Model/SoliditetClient';
import SubaccountHelp from 'Components/SubaccountHelp/SubaccountHelp';
import BackButton from 'Components/CreateRelation/BackButton';
import Preview from '../Preview';
import Tabs, { TABS } from './Tabs/Tabs';
import { AddProvider, useAddDispatch, useAddSelector } from './context/AddContext';
import { selectClient, setActiveTab, setCompanies, setSaving, setSearchQuery, setShowPreview } from './context/Helpers';

import './Add.scss';
import EventBus from 'App/services/EventBus';

type Props = {
	client: DrawerClient;
	closeDrawer: () => void;
	modalId: number;
	goBack: () => void;
	subaccounts: Pick<Client, 'id' | 'name'>[];
	addBranches: (branches: SoliditetClient[] | ProspectingClient[], operationalAccountId: number) => Promise<boolean>;
	pendingPurchase?: PendingPurchaseType | null;
};

export type CompanyRowAddType = {
	company: SelectableClient;
	selected: boolean;
	cantRelate?: boolean;
	onSelectCompany: (company: SelectableClient) => void;
};

export type SelectableClient = Client & { title: string };
type TemplateOption = { id: number; title: string } & { children: SelectableClient[] };

const RESULT_LIMIT = 25;

const getTemplateOptions = (clients: Client[], canRelateAnyCompanyAsSubaccount: boolean) => {
	if (clients === null || clients.length === 0) {
		return [];
	}

	const clientsWithProspectingId = clients
		.filter(client => client.prospectingId)
		.map((client: Client) => ({
			...client,
			title: client.name,
			disabled: true
		}));

	const clientsWithoutProspectingId = clients
		.filter(client => canRelateAnyCompanyAsSubaccount || !client.prospectingId)
		.map((client: Client) => ({
			...client,
			title: client.name,
			disabled: !canRelateAnyCompanyAsSubaccount && !!client.prospectingId
		}));

	const arrayOfClients: TemplateOption[] = [];

	if (clientsWithoutProspectingId.length > 0) {
		arrayOfClients.push({
			id: 1,
			title: T('createRelation.subaccount.optionHeaderTitle1'),
			children: clientsWithoutProspectingId
		});
	}

	if (clientsWithProspectingId.length > 0 && !canRelateAnyCompanyAsSubaccount) {
		arrayOfClients.push({
			id: 2,
			title: T('createRelation.subaccount.optionHeaderTitle2'),
			children: clientsWithProspectingId
		});
	}

	return arrayOfClients;
};

const getFetcher =
	(clientIds: number[], canRelateAnyCompanyAsSubaccount: boolean) =>
	(searchString?: string): Promise<TemplateOption[]> => {
		const filter = new RequestBuilder();

		filter.addFilter(ClientAttributes.active, comparisonTypes.Equals, 1);
		filter.addFilter(ClientAttributes.id, comparisonTypes.NotEquals, clientIds);
		filter.addFilter(ClientAttributes.operationalAccount.attr.id, comparisonTypes.Equals, null);
		filter.addFilter(ClientAttributes.isExternal, comparisonTypes.Equals, 0);
		const orBuilder = filter.orBuilder();
		orBuilder.next();
		orBuilder.addFilter(ClientAttributes.numberOfSubaccounts, comparisonTypes.Equals, 0);
		orBuilder.next();
		orBuilder.addFilter(ClientAttributes.numberOfSubaccounts, comparisonTypes.Equals, null);
		orBuilder.done();

		if (searchString) {
			const searchOrBuilder = filter.orBuilder();
			searchOrBuilder.next();
			searchOrBuilder.addFilter(ClientAttributes.name, comparisonTypes.Search, searchString);
			searchOrBuilder.next();
			searchOrBuilder.addFilter({ field: 'address.city' }, comparisonTypes.Search, searchString);
			searchOrBuilder.next();
			searchOrBuilder.addFilter(ClientAttributes.users.attr.name, comparisonTypes.Search, searchString);
			searchOrBuilder.done();
		}

		filter.addSort(ClientAttributes.name, true);

		filter.limit = RESULT_LIMIT;

		return ClientResource.find(filter.build()).then(({ data }) => {
			return getTemplateOptions(data, canRelateAnyCompanyAsSubaccount);
		});
	};

const mapDispatchToProps = { addBranches: addManyAccounts };

const AddSubaccountContent = ({
	modalId,
	closeDrawer,
	client,
	subaccounts,
	goBack,
	addBranches,
	pendingPurchase
}: Props) => {
	const { unknowns, unbought, config } = useSelector(state => state.Account);
	const isProspecting = config?.id === 'prospecting';
	const { metadata } = useSelector(({ App }) => ({
		metadata: App.metadata
	}));

	const canRelateAnyCompanyAsSubaccount = metadata?.params?.RelateAnySubaccount ?? false;

	const companyRequest = useRef<CancelablePromise<TemplateOption[]> | null>(null);
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

	const dispatch = useAddDispatch();

	const { activeTab, searchQuery, selectedClients, selectedBranches, showPreview, saving } = useAddSelector(
		state => state
	);

	useEffect(() => {
		const fetchCompanies = async () => {
			companyRequest.current?.cancel();

			// Don't fetch if search query is empty
			if (!debouncedSearchQuery.trim()) {
				dispatch(setCompanies([], []));
				return;
			}

			const allClientIdsToSkip = [client.id, ...subaccounts.map(client => client.id)];

			companyRequest.current = makeCancelable(
				getFetcher(allClientIdsToSkip, canRelateAnyCompanyAsSubaccount)(debouncedSearchQuery)
			);

			companyRequest.current.promise
				.then((data: TemplateOption[]) => {
					if (!data) {
						return;
					}

					const initial = {
						selectable: [] as SelectableClient[],
						nonSelectable: [] as SelectableClient[]
					};

					//set selectable and non selectable companies
					const { selectable, nonSelectable } = data.reduce((acc, r) => {
						if (r.id === 1) {
							acc.selectable.push(...r.children);
						} else if (r.id === 2) {
							acc.nonSelectable.push(...r.children);
						}
						return acc;
					}, initial);

					dispatch(setCompanies(selectable, nonSelectable));
				})
				.catch((err: unknown) => {
					logError(err, 'Failed to get companies');
				});
		};

		fetchCompanies();

		return () => companyRequest.current?.cancel();
	}, [debouncedSearchQuery, canRelateAnyCompanyAsSubaccount, client.id, subaccounts]);

	// Wait 300 ms before performing the search
	useEffect(() => {
		const debounce = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);

		return () => {
			if (debounce) {
				clearTimeout(debounce);
			}
		};
	}, [searchQuery]);

	const classes = new BemClass('Add');
	const lang = useMemo(
		() => ({
			save: T('SubaccountDrawer.Add.save'),
			saveOne: T('SubaccountDrawer.Add.saveOne'),
			backButton: T('createRelation.subaccount.backButton', { companyName: client.name }),
			preview: T('createRelation.subaccount.preview'),
			backButtonPreview: T('createRelation.subaccount.backButtonPreview'),
			typeToSearch: T('account.relations.search'),
			selectSubaccount: T('account.subaccount.selectSubaccount'),
			branchOfficeNone: T('addSubaccount.branchOffice.none'),
			selectedOne: T('account.subaccount.selectedOne'),
			selectedMany: T('account.subaccount.selectedMany'),
			unsavedSubaccountsOne: T('SubaccountDrawer.Add.unsavedSubaccountsOne'),
			unsavedSubaccountsMany: T('SubaccountDrawer.Add.unsavedSubaccountsMany')
		}),
		[]
	);
	const cancelRef = useRef<(() => void) | null>(null);
	const isSavingBranches = useRef(false);

	const availableOfficesCount = useMemo(() => {
		if (!unknowns) {
			return 0;
		}

		return unknowns.filter(
			(office: ProspectingClient | SoliditetClient) =>
				!(office as ProspectingClient).matchInUpsales?.operationalAccount && !pendingPurchase
		).length;
	}, [unknowns, pendingPurchase]);

	const hasProspectingId = (
		item: SelectableClient | ProspectingClient | SoliditetClient
	): item is SelectableClient | ProspectingClient => {
		return (item as SelectableClient | ProspectingClient).prospectingId !== undefined;
	};

	const seenProspectingIds = new Set<string>();
	const numberOfSelectedCompanies = [...selectedClients, ...selectedBranches].reduce((acc, client) => {
		if (hasProspectingId(client) && client.prospectingId) {
			if (!seenProspectingIds.has(client.prospectingId)) {
				acc++;
			}
			seenProspectingIds.add(client.prospectingId);
		} else {
			acc++;
		}
		return acc;
	}, 0);

	const addSubaccounts = async () => {
		if (
			selectedBranches.length &&
			selectedBranches.some(branch =>
				unbought.includes(
					isProspecting ? (branch as ProspectingClient).prospectingId : (branch as SoliditetClient).dunsNo
				)
			)
		) {
			isSavingBranches.current = true;
			const addedBranches = await addBranches(selectedBranches, client.id);
			// it means that the did not opt for adding the companies from prospecting
			if (!addedBranches) {
				return;
			}
		}

		const clients = [...selectedClients];
		const clientsSet = new Set(selectedClients.map(({ id }) => id));
		if (selectedBranches.length) {
			for (const branch of selectedBranches) {
				if (
					branch.hasOwnProperty('matchInUpsales') &&
					branch.matchInUpsales &&
					!clientsSet.has(branch.matchInUpsales.id)
				) {
					clientsSet.add(branch.matchInUpsales.id);
					clients.push({ ...branch.matchInUpsales, title: branch.name } as SelectableClient);
				}
			}
		}
		if (!clients.length) {
			goBack();
		}
		// We do not want to spam notifications if multiple accounts are added
		const promises = Array.from(clientsSet).map(id =>
			ClientResource.save(
				{ id, operationalAccount: { id: client.id } },
				{
					skipEvent: true, // This becomes very spammy if you save multiple subaccounts, listen for account.subaccountsAdded instead.
					skipNotification: true // This becomes very spammy if you save multiple subaccounts, not really needed as we have the "banner notification".
				}
			)
		);
		const { promise, cancel } = makeCancelable(Promise.all(promises));
		cancelRef.current = cancel;
		promise
			.then(() => {
				closeDrawer();
				EventBus.broadcast('account.subaccountsAdded', { clients: clients });
			})
			.catch(error => {
				logError(error, 'Failed to add accounts as subaccounts.');
				dispatch(setSaving(false));
			});

		dispatch(setSaving(true));
	};

	useModalClose(
		modalId,
		event => {
			event.preventDefault();

			if (selectedClients.length > 0 && !saving) {
				const body = selectedClients.length === 1 ? lang.unsavedSubaccountsOne : lang.unsavedSubaccountsMany;

				openModal('UnsavedChangesAlert', {
					body,
					confirmButtonText: T('default.goBack'),
					onClose: (confirmed?: boolean) => {
						if (confirmed || confirmed === undefined) {
							return;
						}
						closeDrawer();
					}
				});
			} else {
				closeDrawer();
			}
		},
		[selectedClients, saving]
	);

	const createAccount = () => {
		const onSave = (client?: Client) => {
			if (client && client.id && client.numberOfSubaccounts === 0) {
				dispatch(selectClient({ ...client, title: client.name }));
			}
		};

		if (shouldOpenModal('EditClient')) {
			openModal('EditClient', {
				onClose: onSave,
				prefillData: { name: searchQuery },
				noRedirect: true
			});
		} else {
			const account = Tools.Account.new();
			account.name = searchQuery;

			Tools.$upModal
				.open('editAccount', { fromModal: true, account: account })
				.then(client => {
					onSave(client);
				})
				.catch(error => {
					logError(error, 'Failed to open edit account modal.');
				});
		}
	};

	const saveButtonText = () => {
		switch (numberOfSelectedCompanies) {
			case 0:
				return lang.save;
			case 1:
				return lang.saveOne;
			default:
				return T('SubaccountDrawer.Add.saveMany', { count: numberOfSelectedCompanies });
		}
	};

	const handleChange = (tab: TABS) => {
		dispatch(setActiveTab(tab));

		if (tab === TABS.SELECTED_COMPANIES) {
			dispatch(setSearchQuery(''));
		}
	};

	return (
		<Flex className={classes.b()} direction="column" justifyContent="space-between">
			<Block className={classes.elem('content').b()}>
				<BackButton
					buttonText={showPreview ? lang.backButtonPreview : lang.backButton}
					classes={classes}
					onClick={
						showPreview
							? () => {
									dispatch(setShowPreview(false));
							  }
							: goBack
					}
				/>
				{showPreview ? (
					<Flex direction="column">
						{selectedClients.length || selectedBranches.length ? (
							<Preview selectedClients={selectedClients} selectedBranches={selectedBranches} />
						) : null}
						<SubaccountHelp />
					</Flex>
				) : (
					<Block>
						<Block space="mtxl">
							<Title className={classes.elem('title').b()}>
								{availableOfficesCount === 0
									? lang.branchOfficeNone
									: availableOfficesCount === 1
									? T('addSubaccount.branchOffice.foundOne', { count: availableOfficesCount })
									: T('addSubaccount.branchOffice.foundMany', { count: availableOfficesCount })}
							</Title>
							<PendingPurchaseBanner pendingPurchase={pendingPurchase} space="mtl" />
							<Block
								border="ts ls rs"
								borderColor="grey-4"
								className={classes.elem('search-bar-container').b()}
							>
								<Flex
									className={classes.elem('search-bar').b()}
									space="mtl mrl mll pbl"
									justifyContent="space-between"
									alignItems="center"
									gap="u3"
								>
									<Input
										icon="search"
										className={classes.elem('input-field').b()}
										placeholder={lang.typeToSearch}
										value={searchQuery}
										onChange={e => dispatch(setSearchQuery(e.target.value))}
									/>
									<ButtonSelect
										size="sm"
										options={[
											{ value: TABS.ALL_COMPANIES, title: lang.selectSubaccount },
											{
												value: TABS.SELECTED_COMPANIES,
												title: `${numberOfSelectedCompanies} ${
													numberOfSelectedCompanies === 1
														? lang.selectedOne
														: lang.selectedMany
												}`
											}
										]}
										onChange={handleChange}
										value={activeTab}
									/>
								</Flex>
							</Block>
						</Block>
						<Block border="bs ls rs" borderColor="grey-4" className={classes.elem('tabs-container').b()}>
							<Tabs
								unknowns={unknowns}
								numberOfSelectedCompanies={numberOfSelectedCompanies}
								pendingPurchase={!!pendingPurchase}
								createAccount={createAccount}
							/>
						</Block>
					</Block>
				)}
			</Block>
			<Block
				backgroundColor="grey-1"
				border="ts"
				borderColor="grey-4"
				className={classes.elem('footer').b()}
				space="ptl pbl"
			>
				<Flex alignItems="center" justifyContent="center">
					<PrimaryButton
						disabled={(selectedClients.length === 0 && selectedBranches.length === 0) || saving}
						icon="check"
						onClick={showPreview ? addSubaccounts : () => dispatch(setShowPreview(true))}
						size="lg"
					>
						{showPreview ? saveButtonText() : lang.preview}
					</PrimaryButton>
				</Flex>
			</Block>
		</Flex>
	);
};

const AddSubaccount = ({ modalId, closeDrawer, client, subaccounts, goBack, addBranches, pendingPurchase }: Props) => {
	const { config } = useSelector(state => state.Account);
	const isProspecting = config?.id === 'prospecting';

	return (
		<AddProvider isProspecting={isProspecting}>
			<AddSubaccountContent
				modalId={modalId}
				closeDrawer={closeDrawer}
				client={client}
				subaccounts={subaccounts}
				goBack={goBack}
				addBranches={addBranches}
				pendingPurchase={pendingPurchase}
			/>
		</AddProvider>
	);
};

const Component = connect(null, mapDispatchToProps)(AddSubaccount);
export default Component;
